import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
// StarterKit v3+ already bundles Underline + Link — don't import them separately.
import { Image } from '@tiptap/extension-image';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { Mention } from '@tiptap/extension-mention';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { makeMentionSuggestion } from './MentionSuggestion';
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, Code, Quotes,
  Link as LinkIcon, Image as ImageIcon, Palette, TextStrikethrough, ArrowCounterClockwise, ArrowClockwise,
  TextHOne, TextHTwo, TextHThree, ListChecks, Minus, TextAlignLeft, TextAlignCenter, TextAlignRight,
  Highlighter, CaretDown, Table as TableIcon, RowsPlusBottom, ColumnsPlusRight, Trash,
} from '@phosphor-icons/react';

// A Jira-style rich text editor. Stores HTML. Uploads pasted/chosen images via the
// existing POST /api/upload endpoint. Used for task description and long-text custom
// fields so users can format reproduction steps, implementation plans, etc.
export default function RichEditor({ value, onChange, onBlur, autoFocus, uploadImage, placeholder, minHeight = 140, mentionItems }) {
  // Keep a live reference to mentionItems so the Mention suggestion closure always
  // sees the latest members list (project_members arrive async from parent).
  const mentionItemsRef = useRef(mentionItems || []);
  useEffect(() => { mentionItemsRef.current = mentionItems || []; }, [mentionItems]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        heading: { levels: [1, 2, 3] },
      }),
      Image.extend({
        // Serialize width as an inline style (with px) so it renders reliably in
        // the editor AND the read-only view and survives reload. Height is not
        // serialized — width alone preserves the aspect ratio.
        addAttributes() {
          const toPx = (v) => (v == null || v === '' ? null : (/^\d+(\.\d+)?$/.test(String(v)) ? `${v}px` : String(v)));
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: (el) => {
                const s = el.style?.width;
                if (s) return s;
                const attr = el.getAttribute('width');
                if (!attr) return null;
                return /^\d+$/.test(attr) ? `${attr}px` : attr;
              },
              renderHTML: (attrs) => (attrs.width ? { style: `width: ${toPx(attrs.width)}` } : {}),
            },
            height: {
              default: null,
              parseHTML: (el) => el.style?.height || el.getAttribute('height') || null,
              renderHTML: () => ({}),
            },
          };
        },
      }).configure({
        allowBase64: false,
        HTMLAttributes: { class: 'rte-img' },
        // Drag any corner to resize (aspect ratio locked). Handles are styled
        // globally via [data-resize-handle] in App.css.
        resize: { enabled: true, alwaysPreserveAspectRatio: true, minWidth: 48, minHeight: 32 },
      }),
      TextStyle,
      Color.configure({ types: ['textStyle'] }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      TaskList.configure({ HTMLAttributes: { class: 'rte-task-list' } }),
      TaskItem.configure({ HTMLAttributes: { class: 'rte-task-item' }, nested: true }),
      HorizontalRule,
      Placeholder.configure({ placeholder: placeholder || 'Start writing… use the toolbar above for formatting' }),
      // Table set — header row + resizable columns. We extend the default
      // Table node with a `class` attribute so users can toggle the
      // .rte-table-borderless variant from the toolbar on a per-table
      // basis (the attribute survives serialise/parse via parseHTML/
      // renderHTML below).
      Table.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            class: {
              default: null,
              parseHTML: (el) => el.getAttribute('class'),
              renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
            },
          };
        },
      }).configure({ resizable: true, HTMLAttributes: { class: 'rte-table' } }),
      TableRow,
      TableHeader,
      TableCell,
      Mention.configure({
        HTMLAttributes: { class: 'mention', 'data-testid': 'mention-tag' },
        renderHTML({ options, node }) {
          return [
            'span',
            {
              ...options.HTMLAttributes,
              'data-type': 'mention',
              'data-id': node.attrs.id,
              'data-label': node.attrs.label,
            },
            `@${node.attrs.label ?? node.attrs.id}`,
          ];
        },
        suggestion: makeMentionSuggestion(() => mentionItemsRef.current),
      }),
    ],
    content: value || '',
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none px-6 py-4 text-[14px] leading-relaxed text-zinc-800 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_blockquote]:border-l-4 [&_blockquote]:border-indigo-300 [&_blockquote]:bg-indigo-50/50 [&_blockquote]:pl-4 [&_blockquote]:py-1 [&_blockquote]:italic [&_blockquote]:text-zinc-700 [&_code]:bg-zinc-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12px] [&_code]:font-mono [&_pre]:bg-zinc-900 [&_pre]:text-zinc-50 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-3 [&_img]:max-w-full [&_img]:rounded-lg [&_img]:shadow-sm [&_img]:my-3 [&_a]:text-blue-600 [&_a]:underline [&_hr]:my-4 [&_hr]:border-zinc-200 [&_mark]:bg-yellow-200 [&_mark]:px-1 [&_mark]:rounded-sm [&_.rte-task-list]:list-none [&_.rte-task-list]:pl-0 [&_.rte-task-item]:flex [&_.rte-task-item]:items-start [&_.rte-task-item]:gap-2 [&_.rte-task-item]:my-1 [&_.rte-task-item>label]:flex [&_.rte-task-item>label]:items-center [&_.rte-task-item>label]:pt-0.5 [&_.rte-task-item>label>input]:cursor-pointer [&_.rte-task-item>label>input]:w-4 [&_.rte-task-item>label>input]:h-4 [&_.rte-task-item>label>input]:accent-indigo-600 [&_.rte-task-item>div]:flex-1 [&_.rte-task-item[data-checked="true"]>div]:line-through [&_.rte-task-item[data-checked="true"]>div]:text-zinc-400 [&_.mention]:bg-blue-50 [&_.mention]:text-blue-700 [&_.mention]:px-1 [&_.mention]:py-0.5 [&_.mention]:rounded [&_.mention]:font-medium [&_.mention]:text-[12px] rte-prose [&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_p.is-editor-empty:first-child::before]:text-zinc-400 [&_p.is-editor-empty:first-child::before]:float-left [&_p.is-editor-empty:first-child::before]:pointer-events-none [&_p.is-editor-empty:first-child::before]:h-0',
        style: `min-height: ${minHeight}px;`,
        'data-placeholder': placeholder || 'Start writing…',
        // Enable the browser's native spellcheck (red squiggles + right-click
        // suggestions) on every rich-text field — same UX as Jira / Notion /
        // Google Docs. Browsers respect `spellcheck="true"` on
        // contenteditable elements out of the box; no extra dependency.
        spellcheck: 'true',
        autocorrect: 'on',
        autocapitalize: 'sentences',
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/') && uploadImage) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) insertUploadedImage(file);
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
    onBlur: ({ editor }) => {
      const html = editor.getHTML();
      onBlur?.(html === '<p></p>' ? '' : html);
    },
  });

  // Keep external controlled value in sync (e.g., when cancelling/reopening the editor, or clearing after post).
  useEffect(() => {
    if (!editor) return;
    const external = value || '';
    const current = editor.getHTML();
    const currentNormalized = current === '<p></p>' ? '' : current;
    if (external !== currentNormalized) {
      editor.commands.setContent(external, false);
    }
  }, [value, editor]);

  // Wrapper ref — used to find the closest Radix Sheet/Dialog so we can
  // portal the link dialog INSIDE the Sheet content (so its focus-trap
  // doesn't steal focus from the URL input).
  const editorWrapperRef = useRef(null);

  const insertUploadedImage = async (file) => {
    if (!uploadImage || !editor) return;
    const result = await uploadImage(file);
    if (result?.url) {
      editor.chain().focus().setImage({ src: result.url, alt: file.name || 'image' }).run();
    } else if (result?.error) {
      toast.error(`Image upload failed: ${result.error}`);
    }
  };

  const pickAndUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const f = e.target.files?.[0];
      if (f) insertUploadedImage(f);
    };
    input.click();
  };

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef(null);
  // Capture the editor selection at the moment the user clicks the Link
  // toolbar button. The dialog's URL <input> will steal focus and clear
  // the editor selection, so we need to restore it before applying the
  // mark — otherwise `setLink` runs against an empty range and the link
  // never wraps the selected text.
  const linkSelRef = useRef(null);
  // Radix Sheet/Dialog installs a focus-trap that pulls focus back into
  // its content. Because our link dialog is portaled OUT of the Sheet
  // (intentionally, to escape its CSS transform), the trap steals focus
  // away from the URL input on mount — autoFocus alone doesn't survive.
  // Re-focus a few ticks later with a couple of retries to win the race.
  useEffect(() => {
    if (!linkOpen) return;
    let cancelled = false;
    const retries = [0, 50, 150, 300];
    retries.forEach(ms => setTimeout(() => {
      if (cancelled) return;
      const el = linkInputRef.current;
      if (el && document.activeElement !== el) { el.focus(); el.select(); }
    }, ms));
    return () => { cancelled = true; };
  }, [linkOpen]);
  const setLink = () => {
    if (editor) {
      const { from, to } = editor.state.selection;
      linkSelRef.current = { from, to };
      // If the user has a link active here, prefill its href.
      setLinkUrl(editor.getAttributes('link')?.href || '');
    }
    setLinkOpen(true);
  };
  const applyLink = () => {
    if (!editor) return;
    const sel = linkSelRef.current;
    let chain = editor.chain().focus();
    if (sel && sel.from !== sel.to) {
      chain = chain.setTextSelection(sel);
    }
    if (!linkUrl.trim()) {
      chain.extendMarkRange('link').unsetLink().run();
    } else if (sel && sel.from !== sel.to) {
      // Wrap the previously selected text with the link mark.
      chain.extendMarkRange('link').setLink({ href: linkUrl.trim() }).run();
    } else {
      // No prior selection — insert the URL as a clickable link.
      const url = linkUrl.trim();
      chain.insertContent(`<a href="${url}">${url}</a> `).run();
    }
    linkSelRef.current = null;
    setLinkOpen(false);
  };

  const [colorOpen, setColorOpen] = useState(false);
  const [headingOpen, setHeadingOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  const COLORS = ['#18181b', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#71717a'];
  const HIGHLIGHTS = ['#fef08a', '#fde68a', '#bbf7d0', '#bfdbfe', '#e9d5ff', '#fbcfe8', '#fed7aa', '#fecaca'];

  if (!editor) return <div className="h-20 bg-zinc-50 border border-zinc-200 rounded animate-pulse" />;

  const btn = (opts) => {
    const { onClick, active, title, children, disabled, 'data-testid': tid } = opts;
    return (
      <button type="button" onClick={onClick} title={title} disabled={disabled} data-testid={tid}
        onMouseDown={(e) => e.preventDefault()}
        className={`h-7 min-w-[28px] px-1.5 flex items-center justify-center rounded text-[12px] font-semibold transition-colors ${active ? 'bg-blue-100 text-blue-700' : 'text-zinc-600 hover:bg-zinc-100'} disabled:opacity-40`}>
        {children}
      </button>
    );
  };

  const headingLabel = editor.isActive('heading', { level: 1 }) ? 'H1'
    : editor.isActive('heading', { level: 2 }) ? 'H2'
    : editor.isActive('heading', { level: 3 }) ? 'H3' : 'P';

  // Determine the portal target for the link dialog. If we're rendered
  // inside a Radix Sheet/Dialog (TaskSheet), portal INSIDE the Sheet
  // content so Radix's focus-trap sees the URL input as part of the
  // dialog content (and therefore doesn't steal focus). For full-page
  // editors (TaskDetailPage) fall back to body.
  const linkPortalTarget = (() => {
    if (typeof document === 'undefined') return null;
    if (editorWrapperRef.current) {
      const sheet = editorWrapperRef.current.closest('[role="dialog"]');
      if (sheet) return sheet;
    }
    return document.body;
  })();

  return (
    <div ref={editorWrapperRef} className="border border-zinc-200 rounded-lg bg-white overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all" data-testid="rich-editor">
      <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-zinc-100 bg-zinc-50/60 sticky top-0 z-10" data-testid="rich-editor-toolbar">
        {/* Heading dropdown */}
        <div className="relative">
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setHeadingOpen(v => !v)}
            className="h-7 px-2 inline-flex items-center gap-1 rounded text-[12px] font-semibold text-zinc-600 hover:bg-zinc-100"
            data-testid="rte-heading">
            {headingLabel} <CaretDown size={9} />
          </button>
          {headingOpen && (
            <div className="absolute top-8 left-0 z-30 bg-white border border-zinc-200 rounded-lg shadow-lg p-1 w-32" onMouseDown={(e) => e.preventDefault()}>
              {[
                { id: 'p', label: 'Paragraph', cmd: () => editor.chain().focus().setParagraph().run() },
                { id: 'h1', label: 'Heading 1', icon: TextHOne, cmd: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
                { id: 'h2', label: 'Heading 2', icon: TextHTwo, cmd: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
                { id: 'h3', label: 'Heading 3', icon: TextHThree, cmd: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
              ].map(o => (
                <button key={o.id} onClick={() => { o.cmd(); setHeadingOpen(false); }}
                  className="w-full h-7 px-2 rounded text-left text-[12px] text-zinc-700 hover:bg-zinc-100 inline-flex items-center gap-2" data-testid={`rte-${o.id}`}>
                  {o.icon && <o.icon size={12} weight="bold" />} {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-zinc-200 mx-1" />
        {btn({ onClick: () => editor.chain().focus().toggleBold().run(), active: editor.isActive('bold'), title: 'Bold', 'data-testid': 'rte-bold', children: <TextB size={14} weight="bold" /> })}
        {btn({ onClick: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive('italic'), title: 'Italic', 'data-testid': 'rte-italic', children: <TextItalic size={14} /> })}
        {btn({ onClick: () => editor.chain().focus().toggleUnderline().run(), active: editor.isActive('underline'), title: 'Underline', 'data-testid': 'rte-underline', children: <TextUnderline size={14} /> })}
        {btn({ onClick: () => editor.chain().focus().toggleStrike().run(), active: editor.isActive('strike'), title: 'Strike', children: <TextStrikethrough size={14} /> })}
        {/* Highlight */}
        <div className="relative">
          {btn({ onClick: () => setHighlightOpen(v => !v), active: editor.isActive('highlight'), title: 'Highlight', 'data-testid': 'rte-highlight', children: <Highlighter size={14} /> })}
          {highlightOpen && (
            <div className="absolute top-8 left-0 z-30 bg-white border border-zinc-200 rounded-lg shadow-lg p-1.5 flex gap-1" data-testid="rte-highlight-palette">
              {HIGHLIGHTS.map(c => (
                <button key={c} type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { editor.chain().focus().toggleHighlight({ color: c }).run(); setHighlightOpen(false); }}
                  className="w-5 h-5 rounded border border-zinc-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }} title={c} />
              ))}
              <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => { editor.chain().focus().unsetHighlight().run(); setHighlightOpen(false); }}
                className="w-5 h-5 rounded border border-zinc-200 text-[10px] text-zinc-400" title="Clear">×</button>
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-zinc-200 mx-1" />
        {btn({ onClick: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive('bulletList'), title: 'Bullet list', 'data-testid': 'rte-bullet', children: <ListBullets size={14} weight="bold" /> })}
        {btn({ onClick: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive('orderedList'), title: 'Numbered list', 'data-testid': 'rte-ordered', children: <ListNumbers size={14} weight="bold" /> })}
        {btn({ onClick: () => editor.chain().focus().toggleTaskList().run(), active: editor.isActive('taskList'), title: 'Checklist', 'data-testid': 'rte-tasklist', children: <ListChecks size={14} weight="bold" /> })}
        {btn({ onClick: () => editor.chain().focus().toggleBlockquote().run(), active: editor.isActive('blockquote'), title: 'Quote', children: <Quotes size={14} /> })}
        {btn({ onClick: () => editor.chain().focus().toggleCodeBlock().run(), active: editor.isActive('codeBlock'), title: 'Code block', children: <Code size={14} /> })}
        {btn({ onClick: () => editor.chain().focus().setHorizontalRule().run(), title: 'Divider', 'data-testid': 'rte-hr', children: <Minus size={14} weight="bold" /> })}
        <div className="w-px h-5 bg-zinc-200 mx-1" />
        {/* Alignment */}
        {btn({ onClick: () => editor.chain().focus().setTextAlign('left').run(), active: editor.isActive({ textAlign: 'left' }), title: 'Align left', children: <TextAlignLeft size={14} /> })}
        {btn({ onClick: () => editor.chain().focus().setTextAlign('center').run(), active: editor.isActive({ textAlign: 'center' }), title: 'Align center', children: <TextAlignCenter size={14} /> })}
        {btn({ onClick: () => editor.chain().focus().setTextAlign('right').run(), active: editor.isActive({ textAlign: 'right' }), title: 'Align right', children: <TextAlignRight size={14} /> })}
        <div className="w-px h-5 bg-zinc-200 mx-1" />
        {/* Color */}
        <div className="relative">
          {btn({ onClick: () => setColorOpen(v => !v), active: colorOpen, title: 'Text color', 'data-testid': 'rte-color', children: <Palette size={14} /> })}
          {colorOpen && (
            <div className="absolute top-8 left-0 z-20 bg-white border border-zinc-200 rounded-lg shadow-lg p-1.5 flex gap-1" data-testid="rte-color-palette">
              {COLORS.map(c => (
                <button key={c} type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { editor.chain().focus().setColor(c).run(); setColorOpen(false); }}
                  className="w-5 h-5 rounded-full border border-zinc-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }} title={c} />
              ))}
              <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => { editor.chain().focus().unsetColor().run(); setColorOpen(false); }}
                className="w-5 h-5 rounded-full border border-zinc-200 text-[10px] text-zinc-400" title="Clear">×</button>
            </div>
          )}
        </div>
        {btn({ onClick: setLink, active: editor.isActive('link'), title: 'Link', 'data-testid': 'rte-link', children: <LinkIcon size={14} /> })}
        {uploadImage && btn({ onClick: pickAndUpload, title: 'Insert image', 'data-testid': 'rte-image', children: <ImageIcon size={14} /> })}
        {/* Table — split into "insert" vs "row/column controls" depending on
            whether the cursor is currently inside a table. Mirrors how Notion
            and Confluence handle table editing: one button to insert, contextual
            actions appear once you're inside the cells. */}
        <div className="relative">
          {btn({
            onClick: () => setTableMenuOpen(v => !v),
            active: tableMenuOpen || editor.isActive('table'),
            title: editor.isActive('table') ? 'Table actions' : 'Insert table',
            'data-testid': 'rte-table',
            children: <TableIcon size={14} />,
          })}
          {tableMenuOpen && (
            <div className="absolute top-8 left-0 z-20 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 min-w-[200px]" data-testid="rte-table-menu" onMouseDown={(e) => e.preventDefault()}>
              {!editor.isActive('table') ? (
                <>
                  <button type="button" onClick={() => { editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-100 flex items-center gap-2" data-testid="rte-table-insert-3x3">
                    <TableIcon size={12} /> Insert 3×3 table
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().insertTable({ rows: 2, cols: 4, withHeaderRow: true }).run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-100 flex items-center gap-2" data-testid="rte-table-insert-2x4">
                    <TableIcon size={12} /> Insert 2×4 table
                  </button>
                </>
              ) : (
                <>
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Row</div>
                  <button type="button" onClick={() => { editor.chain().focus().addRowAfter().run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-100 flex items-center gap-2" data-testid="rte-table-add-row">
                    <RowsPlusBottom size={12} /> Add row below
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().addRowBefore().run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-100 flex items-center gap-2">
                    <RowsPlusBottom size={12} className="rotate-180" /> Add row above
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().deleteRow().run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid="rte-table-delete-row">
                    <Trash size={12} /> Delete row
                  </button>
                  <div className="border-t border-zinc-100 my-1" />
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Column</div>
                  <button type="button" onClick={() => { editor.chain().focus().addColumnAfter().run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-100 flex items-center gap-2" data-testid="rte-table-add-col">
                    <ColumnsPlusRight size={12} /> Add column right
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().addColumnBefore().run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-100 flex items-center gap-2">
                    <ColumnsPlusRight size={12} className="rotate-180" /> Add column left
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().deleteColumn().run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid="rte-table-delete-col">
                    <Trash size={12} /> Delete column
                  </button>
                  <div className="border-t border-zinc-100 my-1" />
                  <button type="button" onClick={() => {
                      // Toggle a CSS class on the parent <table>. We commit the
                      // class change via a transaction so it survives serialise/
                      // deserialise round-trips with the saved HTML.
                      const { state, view } = editor;
                      let tablePos = null;
                      state.doc.descendants((node, pos) => {
                        if (node.type.name === 'table' && state.selection.from >= pos && state.selection.from <= pos + node.nodeSize) {
                          tablePos = pos;
                        }
                      });
                      if (tablePos === null) return;
                      const node = state.doc.nodeAt(tablePos);
                      if (!node) return;
                      const currentClass = node.attrs.class || '';
                      const hasBorderless = currentClass.includes('rte-table-borderless');
                      const nextClass = hasBorderless
                        ? currentClass.replace(/\brte-table-borderless\b/g, '').trim()
                        : (currentClass ? `${currentClass} rte-table-borderless` : 'rte-table-borderless');
                      view.dispatch(state.tr.setNodeMarkup(tablePos, undefined, { ...node.attrs, class: nextClass || null }));
                      setTableMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-100 flex items-center gap-2" data-testid="rte-table-toggle-borders">
                    <TableIcon size={12} /> Toggle borders
                  </button>
                  <button type="button" onClick={() => { editor.chain().focus().deleteTable().run(); setTableMenuOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid="rte-table-delete">
                    <Trash size={12} /> Delete table
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex-1" />
        {btn({ onClick: () => editor.chain().focus().undo().run(), disabled: !editor.can().undo(), title: 'Undo', children: <ArrowCounterClockwise size={14} /> })}
        {btn({ onClick: () => editor.chain().focus().redo().run(), disabled: !editor.can().redo(), title: 'Redo', children: <ArrowClockwise size={14} /> })}
      </div>
      <EditorContent editor={editor} />

      {/* Inline link URL dialog — portaled to document.body so Radix Sheet's
          transform animation doesn't capture the `fixed` positioning (a
          transformed ancestor turns `position:fixed` into "fixed relative
          to the ancestor" per CSS spec, which previously clipped the
          dialog inside the slider). */}
      {linkOpen && linkPortalTarget && createPortal(
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40" onClick={() => setLinkOpen(false)} data-testid="rte-link-overlay">
          <div className="bg-white rounded-xl shadow-2xl p-5 w-[420px] max-w-[92vw]" onClick={e => e.stopPropagation()} data-testid="rte-link-dialog" style={{ pointerEvents: 'auto' }}>
            <h3 className="text-base font-semibold text-slate-900 mb-3">Insert / edit link</h3>
            <input
              ref={linkInputRef}
              type="url"
              autoFocus
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyLink(); if (e.key === 'Escape') setLinkOpen(false); }}
              placeholder="https://…"
              className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
            />
            <p className="text-[11px] text-slate-400 mt-2">Leave empty and save to remove the link.</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setLinkOpen(false)} className="h-8 px-3 rounded-lg text-[13px] text-slate-600 hover:bg-slate-100" data-testid="rte-link-cancel">Cancel</button>
              <button onClick={applyLink} className="h-8 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium" data-testid="rte-link-save">Save</button>
            </div>
          </div>
        </div>,
        linkPortalTarget
      )}
      {/* End link dialog portal target switch */}
    </div>
  );
}

// Read-only HTML renderer used when we're NOT in edit mode. The class
// list mirrors the edit-mode editor styles 1:1 so what the user sees
// after save matches what they typed (paragraph spacing, headings,
// task-list checkboxes, blockquote styling, image margins, etc.).
export function RichEditorContent({ html }) {
  if (!html || html === '<p></p>') return <span className="text-zinc-400 text-[13px] italic">Empty</span>;
  return (
    <div
      className="prose prose-sm max-w-none text-[13px] leading-relaxed text-zinc-800 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_blockquote]:border-l-4 [&_blockquote]:border-indigo-300 [&_blockquote]:bg-indigo-50/50 [&_blockquote]:pl-4 [&_blockquote]:py-1 [&_blockquote]:italic [&_blockquote]:text-zinc-700 [&_code]:bg-zinc-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12px] [&_code]:font-mono [&_pre]:bg-zinc-900 [&_pre]:text-zinc-50 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-3 [&_img]:max-w-full [&_img]:rounded-lg [&_img]:shadow-sm [&_img]:my-3 [&_a]:text-blue-600 [&_a]:underline [&_hr]:my-4 [&_hr]:border-zinc-200 [&_mark]:bg-yellow-200 [&_mark]:px-1 [&_mark]:rounded-sm [&_.rte-task-list]:list-none [&_.rte-task-list]:pl-0 [&_.rte-task-item]:flex [&_.rte-task-item]:items-start [&_.rte-task-item]:gap-2 [&_.rte-task-item]:my-1 [&_.rte-task-item>label]:flex [&_.rte-task-item>label]:items-center [&_.rte-task-item>label]:pt-0.5 [&_.rte-task-item>label>input]:w-4 [&_.rte-task-item>label>input]:h-4 [&_.rte-task-item>label>input]:accent-indigo-600 [&_.rte-task-item>label>input]:pointer-events-none [&_.rte-task-item>div]:flex-1 [&_.rte-task-item[data-checked='true']>div]:line-through [&_.rte-task-item[data-checked='true']>div]:text-zinc-400 [&_.mention]:bg-blue-50 [&_.mention]:text-blue-700 [&_.mention]:px-1 [&_.mention]:py-0.5 [&_.mention]:rounded [&_.mention]:font-medium [&_.mention]:text-[12px]"
      data-testid="rich-editor-content"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
